import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Empty,
  PrioSpinner,
  Tabs,
} from '@prio365/prio365-react-library';
import { notification } from 'antd';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getUserMe } from '../../../../../apps/main/rootReducer';
import Flex from '../../../../../components/Flex';
import { ProjectId } from '../../../../../models/Types';
import { makePrioStyles } from '../../../../../theme/utils';
import useAccessRights2 from '../../../../users/hooks/useAccessRights2';

import {
  apiCreateIncomingInvoiceWithoutFile,
  apiDeleteIncomingInvoiceAsCreator,
  apiDeleteIncomingInvoiceAsOffice,
  apiGetIncomingInvoicesAsCreator,
  apiGetIncomingInvoicesAsOffice,
  apiUpdateIncomingInvoiceAsCreator,
  apiUpdateIncomingInvoiceAsOffice,
  apiUploadAndAnalyzeInvoice,
} from '../../../api/index';
import FileDropZone from '../FileDropZone';
import { IncomingInvoiceDrawerContextProvider } from './incomingInvoiceComponents/IncomingInvoiceContextProvider';
import { IncomingInvoicesWidgetCard } from './incomingInvoiceComponents/IncomingInvoicesWidgetCard';
import IncomingInvoiceDrawer from './IncomingInvoiceDrawer';
import { toApiDateUTC } from './incomingInvoiceDateUtils';
import {
  EIncomingInvoiceState,
  IncomingInvoiceDto,
} from '../../../../../models/IncomingInvoices';
import { sanitizeFileName } from '../../../../../util/validateFileName';

const useStyles = makePrioStyles((theme) => ({
  root: {},
  invoiceContainer: {
    overflowY: 'auto',
    maxHeight: '450px',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: theme.spacing.small,
    borderBottom: `1px solid ${theme.colors.application.border}`,
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.small,
    width: '100%',
    fontSize: theme.font.fontSize.small,
    color: theme.colors.application.typography.default,
  },
}));

interface IncomingInvoicesSectionProps {
  projectId: ProjectId;
  isWidgetActive?: boolean; // Indicates if the widget/component is currently active/visible
}

const IncomingInvoicesSection: React.FC<IncomingInvoicesSectionProps> = ({
  projectId,
  isWidgetActive = true,
}) => {
  //#region ------------------------------ Defaults
  const { t } = useTranslation();
  const classes = useStyles();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors

  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const userMe = useSelector(getUserMe);
  const officeId = userMe?.myOfficeId;
  const userMeOfficeRoles = userMe?.prioData.officeRoles ?? {};

  const userMeHasOfficeControllerRole: boolean =
    !!userMeOfficeRoles[officeId]?.find(
      (role) => !!(role === 'officeController')
    ) || false;

  const { checkGlobalRoles, checkProjectRoles } = useAccessRights2();

  const hasGlobalControllerRole = checkGlobalRoles(['globalController']);
  const hasProjectControllerRole = checkProjectRoles(['projectController']);

  const hasAnyControllerPermission =
    hasGlobalControllerRole ||
    userMeHasOfficeControllerRole ||
    hasProjectControllerRole;

  const hasProjectControllerOnly =
    hasProjectControllerRole &&
    !hasGlobalControllerRole &&
    !userMeHasOfficeControllerRole;

  const [myInvoices, setMyInvoices] = useState<IncomingInvoiceDto[]>([]);
  const [officeInvoices, setOfficeInvoices] = useState<IncomingInvoiceDto[]>(
    []
  );

  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);

  const hasProcessingInvoices = useMemo(
    () =>
      myInvoices.some(
        (inv) => inv.incomingInvoiceState === EIncomingInvoiceState.Processing
      ),
    [myInvoices]
  );

  // Filter relevant invoices (exclude approved invoices from previous days)
  const relevantInvoices = useMemo(() => {
    const today = new Date().toDateString();
    return myInvoices.filter((invoice) => {
      if (
        invoice.incomingInvoiceState !== EIncomingInvoiceState.Approved &&
        invoice.incomingInvoiceState !==
          EIncomingInvoiceState.ApprovalRequested &&
        invoice.incomingInvoiceState !== EIncomingInvoiceState.Rejected
      ) {
        return true;
      }
      if (invoice.approvedAt) {
        return new Date(invoice.approvedAt).toDateString() === today;
      }
      return false;
    });
  }, [myInvoices]);

  // Drawer state
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerInvoice, setDrawerInvoice] = useState<IncomingInvoiceDto | null>(
    null
  );

  //#endregion

  //#region ------------------------------ Methods / Handlers
  // Load invoices from API
  const loadInvoices = useCallback(
    async (showLoadingSpinner = false) => {
      try {
        if (showLoadingSpinner) setIsLoading(true);

        const to = new Date();
        const from = new Date();
        from.setMonth(from.getMonth() - 6);

        const myResult = await apiGetIncomingInvoicesAsCreator(from, to);

        if (userMeHasOfficeControllerRole && officeId) {
          const officeResult = await apiGetIncomingInvoicesAsOffice(
            officeId,
            from,
            to
          );
          if (officeResult.result.ok && officeResult.data) {
            setOfficeInvoices(officeResult.data);
          } else {
            notification.error({
              message: t('accounting:incomingInvoices.load.loadFailed'),
              description: t(
                'accounting:incomingInvoices.load.loadFailedDescription'
              ),
            });
          }
        }

        if (myResult.result.ok && myResult.data) {
          setMyInvoices(myResult.data);
        } else {
          if (showLoadingSpinner) {
            notification.error({
              message: t('accounting:incomingInvoices.load.loadFailed'),
              description: t(
                'accounting:incomingInvoices.load.loadFailedDescription'
              ),
            });
          }
        }
      } catch (error) {
        if (showLoadingSpinner) {
          notification.error({
            message: t('accounting:incomingInvoices.load.loadFailed'),
            description: t(
              'accounting:incomingInvoices.load.loadFailedDescription'
            ),
          });
        }
      } finally {
        if (showLoadingSpinner) setIsLoading(false);
      }
    },
    [userMeHasOfficeControllerRole, officeId, t]
  );

  const handleCreateInvoiceWithoutFile = async () => {
    const result = await apiCreateIncomingInvoiceWithoutFile(officeId);

    if (result.result.ok && result.data) {
      notification.success({
        message: t('accounting:incomingInvoices.create.createSuccess'),
        description: t(
          'accounting:incomingInvoices.create.createSuccessDescription'
        ),
      });
      setTimeout(() => {
        loadInvoices(true);
      }, 1500);
    } else {
      notification.error({
        message: t('accounting:incomingInvoices.create.createError'),
        description: t(
          'accounting:incomingInvoices.create.createErrorDescription'
        ),
      });
    }
  };

  const handleDeleteInvoiceAsCreator = useCallback(
    async (invoice: IncomingInvoiceDto) => {
      try {
        const result = await apiDeleteIncomingInvoiceAsCreator(
          invoice.incomingInvoiceId
        );

        if (result.result.ok) {
          setMyInvoices((prev) =>
            prev.filter(
              (inv) => inv.incomingInvoiceId !== invoice.incomingInvoiceId
            )
          );
          notification.success({
            message: t('accounting:incomingInvoices.delete.deleteSuccess'),
            description: t(
              'accounting:incomingInvoices.delete.deleteSuccessDescription'
            ),
          });
        } else {
          throw new Error(`Delete failed: ${result.result.status}`);
        }
      } catch (error) {
        notification.error({
          message: t('accounting:incomingInvoices.delete.deleteError'),
          description: t(
            'accounting:incomingInvoices.delete.deleteErrorDescription'
          ),
        });
      }
    },
    [setMyInvoices, t]
  );

  const handleDeleteInvoiceAsOffice = useCallback(
    async (invoice: IncomingInvoiceDto) => {
      try {
        const result = await apiDeleteIncomingInvoiceAsOffice(
          invoice.incomingInvoiceId,
          officeId
        );

        if (result.result.ok) {
          setOfficeInvoices((prev) =>
            prev.filter(
              (inv) => inv.incomingInvoiceId !== invoice.incomingInvoiceId
            )
          );
          notification.success({
            message: t('accounting:incomingInvoices.delete.deleteSuccess'),
            description: t(
              'accounting:incomingInvoices.delete.deleteSuccessDescription'
            ),
          });
        } else {
          throw new Error(`Delete failed: ${result.result.status}`);
        }
      } catch (error) {
        notification.error({
          message: t('accounting:incomingInvoices.delete.deleteError'),
          description: t(
            'accounting:incomingInvoices.delete.deleteErrorDescription'
          ),
        });
      }
    },
    [setOfficeInvoices, officeId, t]
  );

  // Handle file upload (from button or drop zone)
  const handleFileUpload = async (file: File) => {
    const cleanedFile = file.name
      ? new File([file], sanitizeFileName(file.name), { type: file.type })
      : file;

    try {
      setIsUploading(true);

      // Upload file - expects 204 response with no return values
      const result = await apiUploadAndAnalyzeInvoice(cleanedFile, 'german');

      if (result.result.status === 204 || result.result.ok) {
        notification.success({
          message: t('accounting:incomingInvoices.upload.uploadSuccess'),
          description: t(
            'accounting:incomingInvoices.upload.uploadSuccessDescription'
          ),
        });

        // Reload invoices to show the new analyzing invoice
        await loadInvoices(false);
      } else {
        throw new Error(`Upload failed with status: ${result.result.status}`);
      }
    } catch (error) {
      notification.error({
        message: t('accounting:incomingInvoices.upload.uploadFailed'),
        description: t(
          'accounting:incomingInvoices.upload.uploadFailedDescription'
        ),
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleOpenInvoice = useCallback((invoice: IncomingInvoiceDto) => {
    setDrawerInvoice(invoice);
    setDrawerVisible(true);
  }, []);

  // Get drawer mode based on invoice state
  const getDrawerMode = () => {
    if (!drawerInvoice) return 'edit';

    // Reviewed invoices are read-only
    if (
      drawerInvoice.incomingInvoiceState === EIncomingInvoiceState.Approved ||
      drawerInvoice.incomingInvoiceState === EIncomingInvoiceState.Rejected ||
      drawerInvoice.incomingInvoiceState === EIncomingInvoiceState.Processing
    ) {
      return 'view';
    }

    if (
      drawerInvoice.incomingInvoiceState ===
      EIncomingInvoiceState.ApprovalRequested
    ) {
      return 'review';
    }

    return 'edit';
  };

  // Handle drawer close
  const handleDrawerClose = () => {
    setDrawerVisible(false);
    setTimeout(() => {
      setDrawerInvoice(null);
    }, 300);
  };

  // Handle invoice save
  const handleInvoiceSave = async (invoiceData: IncomingInvoiceDto) => {
    try {
      if (!drawerInvoice) return;

      const updateData = {
        invoiceNumber: invoiceData.invoiceNumber,
        date: toApiDateUTC(invoiceData.date),
        currency: invoiceData.currency,
        totalExcludingVat: invoiceData.totalExcludingVat,
        totalIncludingVat: invoiceData.totalIncludingVat,
        amountToTransfer: invoiceData.amountToTransfer,
        totalVat: invoiceData.totalVat,
        periodOfPerformanceFrom: toApiDateUTC(
          invoiceData.periodOfPerformanceFrom
        ),
        periodOfPerformanceTo: toApiDateUTC(invoiceData.periodOfPerformanceTo),
        cashDiscountPercent: invoiceData.cashDiscountPercent,
        cashDiscountDays: invoiceData.cashDiscountDays,
        notes: invoiceData.notes,
        sharedWithProjectIds: invoiceData.sharedWithProjectIds,
        sharedWithOfficeIds: invoiceData.sharedWithOfficeIds,
        dueDate: toApiDateUTC(invoiceData.dueDate),
        fromOfficeId: invoiceData.fromOfficeId,
        fromOfficeName: invoiceData.fromOfficeName,
        toOfficeId: invoiceData.toOfficeId,
      };

      const drawerMode = getDrawerMode();
      const isReviewMode = drawerMode === 'review';

      const result =
        isReviewMode && officeId
          ? await apiUpdateIncomingInvoiceAsOffice(
              drawerInvoice.incomingInvoiceId,
              officeId,
              updateData
            )
          : await apiUpdateIncomingInvoiceAsCreator(
              drawerInvoice.incomingInvoiceId,
              updateData
            );

      if (result.result.ok && result.data) {
        // Update local state
        if (isReviewMode) {
          setOfficeInvoices((prev) =>
            prev.map((inv) =>
              inv.incomingInvoiceId === drawerInvoice.incomingInvoiceId
                ? result.data!
                : inv
            )
          );
        } else {
          setMyInvoices((prev) =>
            prev.map((inv) =>
              inv.incomingInvoiceId === drawerInvoice.incomingInvoiceId
                ? result.data!
                : inv
            )
          );
        }

        notification.success({
          message: t('accounting:incomingInvoices.save.saveSuccess'),
          description: t(
            'accounting:incomingInvoices.save.saveSuccessDescription'
          ),
        });
      } else {
        throw new Error(`Save failed: ${result.result.status}`);
      }
    } catch (error) {
      notification.error({
        message: t('accounting:incomingInvoices.save.saveFailed'),
        description: t(
          'accounting:incomingInvoices.save.saveFailedDescription'
        ),
      });
    }
  };

  const sortInvoicesByStateAndDate = useCallback(
    (a: IncomingInvoiceDto, b: IncomingInvoiceDto) => {
      const stateOrder = [
        EIncomingInvoiceState.Processing,
        EIncomingInvoiceState.MissingFile,
        EIncomingInvoiceState.Draft,
        EIncomingInvoiceState.Rejected,
        EIncomingInvoiceState.Approved,
      ];
      const aIndex = stateOrder.indexOf(a.incomingInvoiceState);
      const bIndex = stateOrder.indexOf(b.incomingInvoiceState);

      if (aIndex === -1 && bIndex === -1) {
        return (
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      }
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;

      if (aIndex !== bIndex) return aIndex - bIndex;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    },
    []
  );
  //#endregion

  //region ------------------------------ Components
  const renderInvoiceList = useCallback(
    (
      invoices: IncomingInvoiceDto[],
      filterFn: (inv: IncomingInvoiceDto) => boolean,
      isOffice: boolean
    ) => {
      const filtered = invoices.filter(filterFn);
      return (
        <Flex.Column childrenGap={8} className={classes.invoiceContainer}>
          {isLoading ? (
            <Flex.Row justifyContent="center">
              <PrioSpinner size="large" />
            </Flex.Row>
          ) : filtered.length === 0 ? (
            <Empty customMessage={t('accounting:incomingInvoices.empty')} />
          ) : (
            filtered.map((invoice) => (
              <IncomingInvoicesWidgetCard
                key={invoice.incomingInvoiceId}
                invoice={invoice}
                handleOpenInvoice={handleOpenInvoice}
                handleDeleteInvoice={
                  isOffice
                    ? handleDeleteInvoiceAsOffice
                    : handleDeleteInvoiceAsCreator
                }
              />
            ))
          )}
        </Flex.Column>
      );
    },
    [
      classes.invoiceContainer,
      handleOpenInvoice,
      handleDeleteInvoiceAsCreator,
      handleDeleteInvoiceAsOffice,
      isLoading,
      t,
    ]
  );

  const tabItems = [
    {
      key: 'me',
      label: t('accounting:incomingInvoices.tabs.me', {
        count: relevantInvoices.length,
      }),
      content: renderInvoiceList(
        relevantInvoices.sort(sortInvoicesByStateAndDate),
        () => true,
        false
      ),
    },
    ...(userMeHasOfficeControllerRole
      ? [
          {
            key: 'missingReceipt',
            label: t('accounting:incomingInvoices.tabs.missingReceipt', {
              count: officeInvoices.filter(
                (inv) =>
                  inv.incomingInvoiceState === EIncomingInvoiceState.MissingFile
              ).length,
            }),
            content: renderInvoiceList(
              officeInvoices,
              (inv) =>
                inv.incomingInvoiceState === EIncomingInvoiceState.MissingFile,
              true
            ),
          },
          {
            key: 'submits',
            label: t('accounting:incomingInvoices.tabs.submits', {
              count: officeInvoices.filter(
                (inv) =>
                  inv.incomingInvoiceState ===
                  EIncomingInvoiceState.ApprovalRequested
              ).length,
            }),
            content: renderInvoiceList(
              officeInvoices,
              (inv) =>
                inv.incomingInvoiceState ===
                EIncomingInvoiceState.ApprovalRequested,
              true
            ),
          },
        ]
      : []),
  ];
  //endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    if (isWidgetActive && hasProcessingInvoices) {
      pollIntervalRef.current = setInterval(() => {
        loadInvoices(false);
      }, 10000);
    } else if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [isWidgetActive, hasProcessingInvoices, loadInvoices]);

  // Initial load
  useEffect(() => {
    loadInvoices(true);
  }, [loadInvoices]);

  useEffect(() => {
    if (!drawerInvoice) return;

    const updated =
      myInvoices.find(
        (i) => i.incomingInvoiceId === drawerInvoice.incomingInvoiceId
      ) ||
      officeInvoices.find(
        (i) => i.incomingInvoiceId === drawerInvoice.incomingInvoiceId
      );

    if (
      updated &&
      updated.incomingInvoiceState !== drawerInvoice.incomingInvoiceState
    ) {
      setDrawerInvoice(updated);
    }
  }, [myInvoices, officeInvoices, drawerInvoice]);

  //#endregion

  return (
    <IncomingInvoiceDrawerContextProvider>
      <div className={classes.sectionHeader}>
        <div className={classes.sectionTitle}>
          <FontAwesomeIcon icon={['fal', 'file-invoice']} />
          <h4 style={{ marginBottom: 0 }}>
            {t('common:widgetArea.accountingWidget.invoices')}
          </h4>
        </div>
        <Button
          iconProp={['fal', 'plus']}
          type="primary"
          size="small"
          onClick={handleCreateInvoiceWithoutFile}
        ></Button>
      </div>
      <Flex.Column childrenGap={8}>
        {/* Upload Section */}
        <FileDropZone
          onFileUpload={handleFileUpload}
          isUploading={isUploading}
          projectId={projectId}
          acceptedFileTypes={['.pdf', '.jpg', '.jpeg', '.png', '.tiff']}
          size="small"
        />

        {/* Invoices List */}
        <Tabs defaultTabKey="invoices" items={tabItems} />

        {/* Drawer */}
        <IncomingInvoiceDrawer
          visible={drawerVisible}
          onClose={handleDrawerClose}
          onSave={handleInvoiceSave}
          invoice={drawerInvoice}
          projectId={projectId}
          isAnalyzing={
            drawerInvoice?.incomingInvoiceState ===
            EIncomingInvoiceState.Processing
          }
          mode={getDrawerMode()}
          onActionComplete={() => loadInvoices(true)}
          hasAnyControllerPermission={hasAnyControllerPermission}
          hasProjectControllerOnly={hasProjectControllerOnly}
        />
      </Flex.Column>
    </IncomingInvoiceDrawerContextProvider>
  );
};

export default IncomingInvoicesSection;
